import { Client } from '../types/GrooverTypes'

export type PublicActivitiesThemeConfig = {
  backgroundColor: string
  borderColor: string
  textColor: string
  primaryColor: string
  secondaryColor: string
  activityBoxBorder: string
  activityBoxBackground: string
  activityHover: string
  courseBGColor: string
  occationBGColor: string
  otherBGColor: string
}

export type DarkModePreset = 'default' | 'dark-gray' | 'charcoal' | 'black' | 'navy'

export const DARK_MODE_PRESETS: Record<DarkModePreset, string> = {
  default: '#ffffff',
  'dark-gray': '#1a1a1a',
  charcoal: '#2d2d2d',
  black: '#000000',
  navy: '#1a1a2e'
}

/** Legacy default hover — nearly opaque purple; never apply as a card hover fill. */
export const LEGACY_DEFAULT_ACTIVITY_HOVER = 'rgba(108, 108, 158, 0.9)'

export const DEFAULT_PUBLIC_ACTIVITIES_THEME: PublicActivitiesThemeConfig = {
  backgroundColor: 'rgba(224, 217, 254, 0.03)',
  borderColor: '#e0e0e0',
  textColor: '#fff',
  primaryColor: '#2A569D',
  secondaryColor: '#1D5E11',
  activityBoxBorder: '#e0e0e0',
  activityBoxBackground: 'transparent',
  activityHover: LEGACY_DEFAULT_ACTIVITY_HOVER,
  courseBGColor: '#124B8D',
  occationBGColor: '#076712',
  otherBGColor: '#550767'
}

const KNOWN_DARK_BACKGROUNDS = new Set(['#1a1a1a', '#2d2d2d', '#000000', '#1a1a2e', '#000', 'black'])

const parseCssColorToRgb = (color: string): { r: number; g: number; b: number; a: number } | null => {
  const trimmed = color.trim().toLowerCase()
  if (!trimmed || trimmed === 'transparent') return null

  if (trimmed === 'white') return { r: 255, g: 255, b: 255, a: 1 }
  if (trimmed === 'black') return { r: 0, g: 0, b: 0, a: 1 }

  const hexMatch = trimmed.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (hexMatch) {
    const hex = hexMatch[1]
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
        a: 1
      }
    }
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: 1
    }
  }

  const rgbaMatch = trimmed.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/)
  if (rgbaMatch) {
    return {
      r: Number(rgbaMatch[1]),
      g: Number(rgbaMatch[2]),
      b: Number(rgbaMatch[3]),
      a: rgbaMatch[4] !== undefined ? Number(rgbaMatch[4]) : 1
    }
  }

  return null
}

/** True when the color is a solid dark surface (used for page/card bg checks). */
export const isDarkColor = (color: string | undefined | null): boolean => {
  if (!color) return false
  const trimmed = color.trim().toLowerCase()
  if (KNOWN_DARK_BACKGROUNDS.has(trimmed)) return true

  const rgb = parseCssColorToRgb(trimmed)
  if (!rgb || rgb.a < 0.5) return false

  // Relative luminance (sRGB, approximate)
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255
  return luminance < 0.45
}

/**
 * Contrast-safe hover fill for activity cards.
 * Uses the card background (not page background) so a translucent white hover never
 * replaces a solid light card and reveals a dark page underneath.
 */
export const resolveActivityHoverBackground = (theme: PublicActivitiesThemeConfig): string => {
  const configuredHover = theme.activityHover
  const hasCustomHover =
    !!configuredHover && configuredHover !== LEGACY_DEFAULT_ACTIVITY_HOVER && configuredHover.trim() !== ''

  const cardBg = theme.activityBoxBackground
  const surfaceIsDark = isDarkColor(cardBg) || (!cardBg && isDarkColor(theme.backgroundColor))

  if (hasCustomHover) {
    const hoverRgb = parseCssColorToRgb(configuredHover)
    // Translucent light hover on a light card replaces the solid fill → page shows through
    if (hoverRgb && hoverRgb.a < 0.5 && hoverRgb.r > 200 && hoverRgb.g > 200 && hoverRgb.b > 200 && !surfaceIsDark) {
      return 'rgba(0, 0, 0, 0.04)'
    }
    return configuredHover
  }

  return surfaceIsDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
}

const lightenColor = (hex: string, percent: number): string => {
  const cleanHex = hex.replace('#', '')
  if (cleanHex.length !== 6) {
    return '#2a2a2a'
  }

  const num = parseInt(cleanHex, 16)
  if (isNaN(num)) {
    return '#2a2a2a'
  }

  const r = Math.min(255, Math.round((num >> 16) + (255 - (num >> 16)) * percent))
  const g = Math.min(255, Math.round(((num >> 8) & 0x00ff) + (255 - ((num >> 8) & 0x00ff)) * percent))
  const b = Math.min(255, Math.round((num & 0x0000ff) + (255 - (num & 0x0000ff)) * percent))

  const newHex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')
  return `#${newHex}`
}

export const getDarkModeTheme = (
  preset: string,
  baseTheme: PublicActivitiesThemeConfig
): PublicActivitiesThemeConfig => {
  const backgroundColor = DARK_MODE_PRESETS[preset as DarkModePreset] || DARK_MODE_PRESETS['dark-gray']
  const activityBoxBackground = lightenColor(backgroundColor, 0.1)

  return {
    ...baseTheme,
    backgroundColor,
    textColor: '#ffffff',
    activityBoxBackground,
    activityBoxBorder: 'rgba(255, 255, 255, 0.2)',
    activityHover: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.2)'
  }
}

/** Shared theme resolution for CSR (React) and SSR (public activities HTML). */
export const resolvePublicActivitiesThemeConfig = (client: Client): PublicActivitiesThemeConfig => {
  const darkModePreset = client.themeConfig?.darkModePreset

  if (darkModePreset && darkModePreset !== '' && DARK_MODE_PRESETS[darkModePreset as DarkModePreset]) {
    if (darkModePreset === 'default') {
      return {
        ...DEFAULT_PUBLIC_ACTIVITIES_THEME,
        backgroundColor: '#ffffff',
        textColor: '#222222',
        borderColor: '#e0e0e0',
        activityBoxBackground: '#ffffff',
        activityBoxBorder: '#e0e0e0',
        activityHover: 'rgba(0, 0, 0, 0.05)',
        courseBGColor: client.themeConfig?.courseBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.courseBGColor,
        occationBGColor: client.themeConfig?.occationBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.occationBGColor,
        otherBGColor: client.themeConfig?.otherBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.otherBGColor,
        primaryColor: client.themeConfig?.primaryColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.primaryColor,
        secondaryColor: client.themeConfig?.secondaryColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.secondaryColor
      }
    }

    const baseTheme: PublicActivitiesThemeConfig = {
      ...DEFAULT_PUBLIC_ACTIVITIES_THEME,
      courseBGColor: client.themeConfig?.courseBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.courseBGColor,
      occationBGColor: client.themeConfig?.occationBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.occationBGColor,
      otherBGColor: client.themeConfig?.otherBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.otherBGColor,
      primaryColor: client.themeConfig?.primaryColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.primaryColor,
      secondaryColor: client.themeConfig?.secondaryColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.secondaryColor
    }
    return getDarkModeTheme(darkModePreset, baseTheme)
  }

  return {
    backgroundColor: client.themeConfig?.backgroundColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.backgroundColor,
    borderColor: client.themeConfig?.borderColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.borderColor,
    textColor: client.themeConfig?.textColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.textColor,
    primaryColor: client.themeConfig?.primaryColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.primaryColor,
    secondaryColor: client.themeConfig?.secondaryColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.secondaryColor,
    activityBoxBorder: client.themeConfig?.activityBoxBorder || DEFAULT_PUBLIC_ACTIVITIES_THEME.activityBoxBorder,
    activityBoxBackground:
      client.themeConfig?.activityBoxBackground || DEFAULT_PUBLIC_ACTIVITIES_THEME.activityBoxBackground,
    activityHover: client.themeConfig?.activityHover || DEFAULT_PUBLIC_ACTIVITIES_THEME.activityHover,
    courseBGColor: client.themeConfig?.courseBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.courseBGColor,
    occationBGColor: client.themeConfig?.occationBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.occationBGColor,
    otherBGColor: client.themeConfig?.otherBGColor || DEFAULT_PUBLIC_ACTIVITIES_THEME.otherBGColor
  }
}
